<script>
import { removeAccents } from '@utils/Format'
import { sortClientsByName } from '@utils/Sort'
import { GlobalBus } from '@services/bus'
import { FEATURES } from '@constants/Text'
import { apiCrmGrupoFamiliar } from '@modules/crm/api/apiCrmGrupoFamiliar'
import FamilyGroupSearch from '@modules/consolidator/components/family-group-search/family-group-search.vue'
import {
  readSelectedGroupId,
  readSelectedMode,
  writeSelectedGroupId,
  writeSelectedMode,
} from '@modules/consolidator/utils/group-selection-storage/group-selection-storage'
import { formatBrokerCode } from '@utils/cliente-codigo/cliente-codigo'
import { FILTER_MODE } from '@modules/consolidator/constants/filter-mode'
import { emailDoCliente } from '@utils/clienteEmail'

export default {
  name: 'ClientSearch',
  components: { FamilyGroupSearch },
  inheritAttrs: false,
  props: {
    allowFamilyGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchClientTxt: '',
      filterMode: FILTER_MODE.CLIENTES,
      loadingGroups: false,
    }
  },
  computed: {
    clientes() {
      return sortClientsByName(this.$store.getters['user/allClients'] || [])
    },
    activeClientIsLead() {
      const client = this.$store.getters['user/currentClient']
      return !!(client && client.cliente_prospect)
    },
    filteredClientes() {
      if (this.filterMode === FILTER_MODE.LEADS) {
        return this.clientes.filter((c) => c.cliente_prospect)
      }
      return this.clientes.filter((c) => !c.cliente_prospect)
    },
    isFamilyGroupMode() {
      return this.filterMode === FILTER_MODE.FAMILIAS
    },
    grupos() {
      return this.$store.getters['crm/gruposFamiliaresListagem'] || []
    },
    currentFamilyGroup() {
      return this.$store.getters['consolidator/currentFamilyGroup'] || null
    },
    modoNaStore() {
      return (
        this.$store.getters['consolidator/walletSelectorMode'] ||
        FILTER_MODE.CLIENTES
      )
    },
    canSelectFamilyGroup() {
      if (!this.allowFamilyGroup) return false
      const show = this.$store.getters['parameters/show'] || {}
      const user = this.$store.getters['user/currentUser'] || {}
      return !!show[FEATURES.FAMILY_GROUP] && !user.IS_CLIENTE
    },
    // O e-mail exibido vem derivado na linha, e não de `emailDoCliente(item)`
    // chamado no template: interpolação com chamada de função re-executa a
    // cada render, uma vez por item da lista.
    //
    // Aqui o pré-cômputo é seguro — e nos outros seletores de cliente não é.
    // Este autocomplete usa `item-value="id"`: o `v-model` devolve o id, e
    // `search()` resolve esse id contra `clientes`, que continua sendo a lista
    // da store sem cópia. Logo o objeto que `selectClient()` despacha é o
    // original, não a cópia rasa daqui.
    clientesComEmail() {
      return this.filteredClientes.map((cliente) => {
        return { ...cliente, emailExibido: emailDoCliente(cliente) }
      })
    },
    toggleOptions() {
      const options = [
        { value: FILTER_MODE.CLIENTES, text: 'Clientes' },
        { value: FILTER_MODE.LEADS, text: 'Leads' },
      ]
      if (this.canSelectFamilyGroup) {
        options.push({ value: FILTER_MODE.FAMILIAS, text: 'Famílias' })
      }
      return options
    },
  },
  watch: {
    filterMode: {
      immediate: true,
      handler(modo) {
        if (!this.allowFamilyGroup) return
        this.sincronizarModoNaStore(modo)
      },
    },
    modoNaStore(modo) {
      if (!this.allowFamilyGroup) return
      if (modo === this.filterMode) return
      this.filterMode = modo
    },
    isFamilyGroupMode: {
      immediate: true,
      handler(ativo) {
        if (ativo) this.loadFamilyGroups()
      },
    },
    activeClientIsLead: {
      immediate: true,
      handler(isLead) {
        if (this.filterMode === FILTER_MODE.FAMILIAS) return
        this.filterMode = isLead ? FILTER_MODE.LEADS : FILTER_MODE.CLIENTES
      },
    },
  },
  created() {
    this.restoreSelectedMode()
  },
  methods: {
    async loadFamilyGroups() {
      if (this.loadingGroups) return
      if (this.grupos.length) {
        this.restoreSelectedGroup()
        return
      }
      this.loadingGroups = true
      try {
        const r = await apiCrmGrupoFamiliar.carregarListagem({})
        if (r && r.status !== 200) this.$showSnack(r)
        this.restoreSelectedGroup()
      } finally {
        this.loadingGroups = false
      }
    },
    sincronizarModoNaStore(modo) {
      this.$store.dispatch('consolidator/setWalletSelectorMode', modo)
    },
    restoreSelectedMode() {
      if (!this.canSelectFamilyGroup) return
      const user = this.$store.getters['user/currentUser'] || {}
      if (readSelectedMode(user.id) !== FILTER_MODE.FAMILIAS) return
      this.filterMode = FILTER_MODE.FAMILIAS
    },
    restoreSelectedGroup() {
      if (this.currentFamilyGroup) return
      const user = this.$store.getters['user/currentUser'] || {}
      const savedId = readSelectedGroupId(user.id)
      if (!savedId) return
      const grupo = this.grupos.find((g) => String(g.id) === String(savedId))
      if (!grupo) return
      this.aplicarGrupoComoContexto(grupo)
    },
    selectFamilyGroup(grupo) {
      if (!grupo) return
      const user = this.$store.getters['user/currentUser'] || {}
      writeSelectedGroupId(user.id, grupo.id)
      writeSelectedMode(user.id, FILTER_MODE.FAMILIAS)
      this.aplicarGrupoComoContexto(grupo)
    },
    aplicarGrupoComoContexto(grupo) {
      this.$store.dispatch('consolidator/selectFamilyGroup', grupo)
      this.$store.dispatch('user/addSearchClient', { id: null, name: '' })
      this.$store.dispatch('navigation/backToGestao')
      GlobalBus.$emit('changeClient')
    },
    customFilter(item, queryText) {
      const name = removeAccents((item.name || '').toLowerCase())
      // `emailDoCliente` preserva a caixa (exibe `CLI_42` como está no banco);
      // quem compara é a busca, então ela baixa a caixa dos dois lados.
      const email = emailDoCliente(item).toLowerCase()
      const codigoCorretora = formatBrokerCode(item.codigo_corretora).toLowerCase()
      const searchText = removeAccents((queryText || '').toLowerCase())

      return (
        name.indexOf(searchText) > -1 ||
        email.indexOf(searchText) > -1 ||
        codigoCorretora.indexOf(searchText) > -1
      )
    },
    search(_id) {
      const c = this.clientes.find((c) => {
        return c.id === _id
      })
      setTimeout(() => {
        this.searchClientTxt = -1
      }, 0)
      if (c) {
        this.selectClient(c)
      }
    },
    selectClient(client) {
      this.$store.dispatch('user/addSearchClient', client)
      const user = this.$store.getters['user/currentUser'] || {}
      writeSelectedMode(user.id, this.modoNaStore)
      GlobalBus.$emit('changeClient')
    },
  },
}
</script>

<template>
  <div>
    <BaseToggleButton
      v-model="filterMode"
      :options="toggleOptions"
      small
      button-style="flatGray"
      class="mb-1"
    />
    <FamilyGroupSearch
      v-if="isFamilyGroupMode"
      ref="familyGroupSearch"
      v-bind="$attrs"
      :value="currentFamilyGroup"
      :grupos="grupos"
      :loading="loadingGroups"
      @select="selectFamilyGroup"
    />
    <v-autocomplete
      v-else
      id="client-search"
      ref="campo"
      v-model="searchClientTxt"
      v-bind="$attrs"
      :placeholder="
        filterMode === 'leads' ? 'Selecione um lead' : 'Selecione um cliente'
      "
      style="max-width: 180px"
      rounded
      class="p-0 new-ux-scroll client-search__field"
      :items="clientesComEmail"
      item-value="id"
      item-text="name"
      autocomplete="off"
      :filter="customFilter"
      v-on="$listeners"
      @change="search"
    >
      <template v-slot:item="data">
        <v-list-item-content style="display: table-column">
          <v-row style="padding: 5px; align-items: center">
            <v-icon
              v-if="!data.item || !data.item.id_cadastro_cliente"
              small
              right
              color="primary"
            >
              mdi-account
            </v-icon>
            <v-icon v-else small right> mdi-account-switch </v-icon>
            <p style="margin-bottom: 0px; margin-left: 5px">
              {{ data.item ? data.item.name : '' }}
            </p>
          </v-row>
          <v-row>
            <small style="margin-left: 15px" class="text-secondary">
              {{ data.item ? data.item.emailExibido : '' }}
            </small>
          </v-row>
        </v-list-item-content>
      </template>
    </v-autocomplete>
  </div>
</template>

<style lang="sass" scoped>
// `rounded` no v-autocomplete acima esconde o underline padrão do Vuetify
// (regra dele mesmo: --solo/--outlined/--full-width/--rounded removem o
// :before/:after de underline) mas também aplica o raio pill (28px). Aqui
// restauramos o raio padrão dos controles do DS (var(--radius-md), mesmo
// token de botão/segmented/demais inputs em _controls.sass +
// style-rounded.sass), mantendo o `rounded` só para suprimir o underline.
.client-search__field
  &.v-text-field--rounded
    border-radius: var(--radius-md) !important
    ::v-deep
      fieldset
        border-radius: var(--radius-md) !important
      > .v-input__control > .v-input__slot
        border-radius: var(--radius-md) !important
        padding: 0 12px !important
</style>
